<template>
  <div v-if="!ready">
    <ProgressSpinner />
  </div>
  <div v-else-if="noLeagues" style="margin-top: 20vh;">
    <div class="p-grid" style="max-width: 1000px; margin: 0 auto">
      <div class="p-col-12">
        <h1 class="title is-1">{{ $t("please-create-a-league-to-continue") }}</h1>
      </div>
      <div class="p-col-4">
        <div class="p-mb-2 p-mr-3">
          <div class="bigButton expand" @click="startSportsEngineAuth">
            <img class="centeredGif" :src="`${baseURL}/sportsEngine_icon-logo-white.svg`" />
            <br />
            {{ $t("import-from-sportsengine") }}
          </div>
        </div>
      </div>
      <div class="p-col-4">
        <div class="p-mb-2 p-mr-3">
          <div class="bigButton expand" @click="goRouter('/wizard')" :label="$t('start-from-scratch')">
            <font-awesome-icon class="cursorpointer" :icon="['fal', 'calendar']" /><br />
            {{ $t("use-league-wizard") }}
          </div>
        </div>
      </div>
      <div class="p-col-4">
        <div class="p-mb-2 p-mr-3">
          <div class="bigButton expand" @click="goRouter('/leagues/new')" :label="$t('create-manual-league')">
            <font-awesome-icon class="cursorpointer" :icon="['fal', 'calendar']" /><br />
            {{ $t("create-manual-league") }}
          </div>
        </div>
      </div>
      <div class="p-col-12">
        <div class="p-mb-2 p-mr-3 p-mt-5">
          <a v-show="showSwitcher" @click="nextOrganization()"> {{ $t("switch-organization") }}</a>
        </div>
      </div>
    </div>
  </div>
  <div class="p-grid paddedCol p-text-left" v-else-if="currentSeason">
    <UpgradeBanner v-if="currentUser && !currentUser.upgraded" />
    <div class="p-col-12" v-if="!currentUser.upgraded">
      <br />
    </div>
    <Dialog
      :header="$t('Dashboard.timezone_dialog.header')"
      :visible.sync="timezoneDialog"
      :style="{ width: 'auto' }"
      :modal="true"
    >
      <div class="p-grid p-fluid" style="max-width: 400px">
        <div class="p-field p-col-12">
          {{ $t("Dashboard.timezone_dialog.text") }}
        </div>
        <div class="p-field p-col-12">
          <router-link to="/account"><Button :label="$t('Button.go_to_account')"/></router-link>
        </div>
      </div>
    </Dialog>
    <div class="p-col-12">
      <div class="title is-1">{{ $t("welcome") }}</div>
    </div>
    <div class="p-col-12">
      <p>{{ $t('how-to-create-a-schedule') }}</p>
      <ul class="dashboard-howto-list">
        <li v-html="$t('create-your-divisions-and-teams')">
        </li>
        <li v-html="$t('create-your-venues')"></li>
        <li>
          <span v-html="$t('create-your-timeslots')"></span>
          <a
            href="https://support.cactusware.com/"
            target="_blank"
          >
            {{ $t('view-tutorial') }}
          </a>
        </li>
        <li>
          <span v-html="$t('create-your-schedules')"></span>
          <a
            href="https://support.cactusware.com/"
            target="_blank"
            > {{ $t('view-tutorial') }}</a
          >
        </li>
      </ul>
      <div class="text-muted fs-small-14 mt-1">
        <span v-html="$t('tutorials.instruction', {
          aLink: this.currentUser.locale === 'ja' ? `<a href='https://support.diamondscheduler.jp/' target='_blank'>Knowledge Base</a>` : `<a href='https://support.cactusware.com/' target='_blank'>Knowledge Base</a>`,
          emailLink: this.currentUser.locale === 'ja' ? `<a href='mailto:support@diamondscheduler.jp'>support@diamondscheduler.jp</a>` : `<a href='mailto:help@cactusware.com'>help@cactusware.com</a>` 
        })"></span>
      </div>
    </div>
    <div class="p-col-12 p-my-3">
      <InputText
        autofocus="true"
        v-model="searchString"
        id="bigSearch"
        class="rounded"
        :placeholder="this.$t('Dashboard.searchString')"
      />
    </div>
    <div class="p-col-12 paddedCol whiteBg rounded " v-if="searchString">
      <div v-if="searching" class="p-text-center">
        <h1 class="title is-3">{{ $t("searching") }}</h1>
        <ProgressSpinner />
      </div>
      <div v-else-if="searchSeason.count == 0">
        {{ $t("no-results-try-another-phrase") }}
        <br />
        <Button
          style=""
          class="bold p-button-text p-button-secondary p-button-lg"
          @click="searchString = ''"
          :label="$t('clear-search')"
        />
      </div>
      <div v-else-if="searchSeason && currentSeason" class="p-grid searchResults">
        <div class="p-col-12">
          <h1 class="title is-3 displayInline">{{ $t("results-searchseason-count", [searchSeason.count]) }}</h1>
          <Button
            style="float: right"
            class="bold p-button-text p-button-secondary p-button-lg"
            @click="searchString = ''"
            :label="$t('clear-search')"
          />
        </div>
        <div class="p-col-4" v-if="searchSeason.divisions.length">
          <h2 class="title is-5">
            {{ $t("divisions-searchseason-divisions-length", [searchSeason.divisions.length]) }}
          </h2>
          <div v-for="x in searchSeason.divisions" :key="x.id">
            <router-link :to="`/divisions/${x.id}`"> {{ x.name }} </router-link>
          </div>
        </div>
        <div class="p-col-4" v-if="searchSeason.teams.length">
          <h2 class="title is-5">{{ $t("teams-d-teams-length", [searchSeason.teams.length]) }}</h2>
          <div v-for="x in searchSeason.teams" :key="x.id">
            <router-link :to="`/teams/${x.id}`"> {{ x.name }} </router-link>
          </div>
        </div>
        <div class="p-col-4" v-if="searchSeason.schedules.length">
          <h2 class="title is-5">
            {{ $t("schedules-searchseason-schedules-length", [searchSeason.schedules.length]) }}
          </h2>
          <div v-for="x in searchSeason.schedules" :key="x.id">
            <router-link :to="`/schedules/${x.id}`"> {{ x.name }} </router-link>
          </div>
        </div>
        <div class="p-col-4" v-if="searchSeason.people.length">
          <h2 class="title is-5">{{ $t("people-searchseason-people-length", [searchSeason.people.length]) }}</h2>
          <div v-for="x in searchSeason.people" :key="x.id">
            <router-link :to="`/people/${x.id}`"> {{ x.first }} {{ x.last }} </router-link>
          </div>
        </div>
        <div class="p-col-4" v-if="searchSeason.venues.length">
          <h2 class="title is-5">{{ $t("venues-searchseason-venues-length", [searchSeason.venues.length]) }}</h2>
          <div v-for="x in searchSeason.venues" :key="x.id">
            <router-link :to="`/venues/${x.id}`"> {{ x.name }} </router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="p-col-4 dashboard-accordions" v-if="!searchString">
      <Accordion>
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("Division.divisions") }}
                <div class="title is-3 mb-0">{{ currentSeason.divisions.length }}</div>
              </div>
              <Button @click.stop="goRouter('/divisions/new')">{{ $t("Button.create") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.divisions.length">
            <div v-for="d in currentSeason.divisions" :key="d.id">
              <p>
                <router-link :to="`/divisions/${d.id}`">{{ d.name }}</router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-divisions-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>

      <Accordion class="p-mt-5">
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("Venue.venues") }}
                <div class="title is-3 mb-0">{{ currentSeason.venues.length }}</div>
              </div>
              <Button @click.stop="goRouter('/venues/new')">{{ $t("AppMenu.create_button") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.venues.length">
            <div v-for="v in currentSeason.venues" :key="v.id">
              <p>
                <router-link :to="`/venues/${v.id}`">{{ v.name }}</router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-venues-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>
    </div>

    <div class="p-col-4 dashboard-accordions" v-if="!searchString && currentSeason">
      <Accordion>
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("Team.teams") }}
                <div class="title is-3 mb-0">{{ currentSeason.teams.length }}</div>
              </div>
              <Button @click.stop="goRouter('/teams/new')">{{ $t("AppMenu.create_button") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.teams.length">
            <div v-for="t in currentSeason.teams" :key="t.id">
              <p>
                <router-link :to="`/teams/${t.id}`">{{ t.name }}</router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-teams-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>

      <Accordion class="p-mt-5">
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("People.people") }}
                <div class="title is-3 mb-0">{{ currentSeason.people.length }}</div>
              </div>
              <Button @click.stop="goRouter('/people/new')">{{ $t("AppMenu.create_button") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.people.length">
            <div v-for="p in currentSeason.people" :key="p.id">
              <p>
                <router-link :to="`/people/${p.id}`">{{ p.first }} {{ p.last }} </router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-people-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>
    </div>

    <div class="p-col-4 dashboard-accordions" v-if="!searchString && currentSeason">
      <Accordion>
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("Schedule.schedules") }}
                <div class="title is-3 mb-0">{{ currentSeason.schedules.length }}</div>
              </div>
              <Button @click.stop="goRouter('/schedules/new')">{{ $t("AppMenu.create_button") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.schedules.length">
            <div v-for="s in currentSeason.schedules" :key="s.id">
              <p>
                <router-link :to="`/schedules/${s.id}`">{{ s.name }}</router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-schedules-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>

      <Accordion class="p-mt-5">
        <AccordionTab>
          <template slot="header">
            <div class="accordion-header">
              <div class="strongHeader">
                {{ $t("Tournaments.tournaments") }}
                <div class="title is-3 mb-0">{{ currentSeason.tournaments.length }}</div>
              </div>
              <Button @click.stop="goRouter('/tournaments/new')">{{ $t("AppMenu.create_button") }}</Button>
            </div>
          </template>

          <div v-if="currentSeason.tournaments.length">
            <div v-for="t in currentSeason.tournaments" :key="t.id">
              <p>
                <router-link :to="`/schedules/${t.id}`">{{ t.name }}</router-link>
              </p>
            </div>
          </div>
          <div v-else>
            <i>{{ $t("no-tournaments-created") }}</i>
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  </div>
</template>

<script>
import api from "@/api";

export default {
  // dddd
  data() {
    return {
      timezoneDialog: false,
      ready: false,
      leagues: [],
      isLoading: true,
      searchString: "",
      searching: false,
      searchTimer: null,
    };
  },
  computed: {
    searchSeason() {
      if (!this.currentSeason) {
        this.refreshLeagueInformation();
        return null;
      }
      var count = 0;
      if (!this.searchString) {
        return this.currentSeason;
      }

      var results = {
        divisions: [],
        teams: [],
        venues: [],
        people: [],
        venues: [],
        schedules: [],
        count: 0,
      };

      // Divisions
      this.currentSeason.divisions.forEach(d => {
        if ((d.name || "").toLowerCase().includes(this.searchString.toLowerCase())) {
          results.divisions.push(d);
          count++;
        }
      });
      // Teams
      this.currentSeason.teams.forEach(d => {
        if ((d.name || "").toLowerCase().includes(this.searchString.toLowerCase())) {
          results.teams.push(d);
          count++;
        }
      });
      // Venues
      this.currentSeason.venues.forEach(d => {
        if ((d.name || "").toLowerCase().includes(this.searchString.toLowerCase())) {
          results.venues.push(d);
          count++;
        }
      });
      // Schedules
      this.currentSeason.schedules.forEach(d => {
        if ((d.title || "").toLowerCase().includes(this.searchString.toLowerCase())) {
          results.schedules.push(d);
          count++;
        }
      });
      // People;
      this.currentSeason.people.forEach(d => {
        if ((d.first + d.last).toLowerCase().includes(this.searchString.toLowerCase())) {
          count++;
          results.people.push(d);
        }
      });
      results.count = count;
      return results;
    },
    showSwitcher() {
      const organizationInformation = this.$store.getters.organizationInformation;
      return Array.isArray(organizationInformation) && organizationInformation.length > 1;
    },
  },
  watch: {
    searchString: function() {
      this.searching = true;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searching = false;
      }, 600);
    },
  },
  methods: {
    nextOrganization() {
      if (this.$store.getters.organizationInformation.length > 1) {
        this.$store.getters.organizationInformation.forEach(org => {
          if (org.id != this.currentOrganization.id) {
            this.switchOrganization(org);
            this.refreshLeagueInformation();
            this.goRouter("/");
          }
        });
      }
    },
  },
  created() {
    this.refreshLeagueInformation();
    api.getLeagues().then(response => {
      this.leagues = response.data;
      this.isLoading = false;
      setTimeout(() => {
        this.ready = true;
      }, 1500);
    });
  },
  mounted() {
    window.analytics.page();
    if (!this.currentUser.timezone) {
      this.timezoneDialog = true;
    }
  },
};
</script>

<style scoped lang="scss">
#bigSearch {
  width: 100%;
  background: white;
  font-size: 1.2em;
  padding: 10px 20px;
}
.searchResults {
  font-size: 1.5em;
}
</style>
